import { useCallback } from 'react';
import { canCompleteTask } from '../utils/sequentialTasks';

/**
 * Hook for handling task status changes
 * @param {Object} params - Configuration object
 * @param {Array} params.tasks - All tasks
 * @param {Function} params.updateTaskStatus - Function to update task status
 */
export function useTaskStatus({
  tasks,
  updateTaskStatus
}) {
  /**
   * Handle checkbox change or direct status change
   * @param {string} taskId - The task ID (database ID)
   * @param {string|boolean} newStatusOrCurrentStatus - New status string or current status for toggle
   */
  const handleCheckboxChange = useCallback(async (taskId, newStatusOrCurrentStatus, origin) => {
    const task = tasks.find(t => t.id === taskId);
    if (!task) return;
    
    // If newStatusOrCurrentStatus is one of the valid statuses, use it directly
    // Otherwise, treat it as currentStatus and toggle between complete/incomplete
    const validStatuses = ['complete', 'incomplete', 'wip', 'waiting', 'wont_do'];
    let newStatus;
    
    // Check if the parameter is a valid status string
    if (typeof newStatusOrCurrentStatus === 'string' && validStatuses.includes(newStatusOrCurrentStatus)) {
      // This is a direct status change (e.g., from dropdown)
      newStatus = newStatusOrCurrentStatus;
    } else {
      // This is a toggle from checkbox click (or invalid parameter)
      const currentStatus = task.status;
      newStatus = currentStatus === 'complete' || currentStatus === 'wip' || currentStatus === 'waiting' || currentStatus === 'wont_do'
        ? 'incomplete' 
        : 'complete';
    }
    
    // If trying to complete a task, check if it can be completed
    // (this includes checking sequential tasks and tasks blocked by parent sequential tasks)
    // Note: 'wont_do' counts as complete for progress but doesn't need sequential validation
    if (newStatus === 'complete' || newStatus === 'wip' || newStatus === 'waiting') {
      if (!canCompleteTask(taskId, tasks)) {
        // Don't allow completion if task is blocked
        return;
      }
    }
    
    updateTaskStatus(taskId, newStatus, origin);
  }, [tasks, updateTaskStatus]);

  return { handleCheckboxChange };
}
